import { useEffect, useRef } from 'react';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea, [tabindex]';

/**
 * Удерживает фокус внутри окна и возвращает его на прежний элемент при закрытии.
 * Без этого пользователь с клавиатуры уходит на страницу партнёра под подложкой.
 */
export function useFocusTrap<T extends HTMLElement>(isActive = true) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const host = ref.current;
    if (!isActive || !host) return;

    const previous = document.activeElement as HTMLElement | null;
    host.querySelector<HTMLElement>(FOCUSABLE)?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const items = Array.from(host.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;

      const first = items[0]!;
      const last = items[items.length - 1]!;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    host.addEventListener('keydown', onKeyDown);
    return () => {
      host.removeEventListener('keydown', onKeyDown);
      previous?.focus();
    };
  }, [isActive]);

  return ref;
}
